import React, { useState } from 'react';
import { Check } from 'lucide-react';
import T from 'Components/Helpers/translate';
import ContactResource from 'Resources/Contact';
import type Contact from 'App/resources/Model/Contact';
import type { FoundContactDetails } from './FindContactDetails';

type State = 'idle' | 'saving' | 'filled' | 'nothingToFill' | 'error';

/**
 * "Add to the contact" under Find email and phone, when the profile already is a contact
 * (WEB-13446). Fills only what the contact lacks - the email, and the mobile number in `cellPhone`,
 * where the Add flow puts a found phone too - and never overwrites a value someone entered. Shown,
 * off, from before the lookup, and each state in one slot of the same height (WEB-15192).
 */
type Props = { contactId: number; found: FoundContactDetails; onFilled?: () => void };

const FillExistingContact: React.FC<Props> = ({ contactId, found, onFilled }) => {
	const [state, setState] = useState<State>('idle');

	const fill = async () => {
		setState('saving');
		try {
			const { data: contact } = await ContactResource.get(contactId);
			const missing: Partial<Contact> = {
				...(found.email && !contact.email ? { email: found.email } : {}),
				...(found.phone && !contact.cellPhone ? { cellPhone: found.phone } : {})
			};
			if (!Object.keys(missing).length) {
				setState('nothingToFill');
				return;
			}
			await ContactResource.save({ id: contactId, ...missing });
			setState('filled');
			onFilled?.();
		} catch (err) {
			console.error('[EmbedAIChat] filling the contact failed', err);
			setState('error');
		}
	};

	return (
		<div className="embed-add-fill">
			{state === 'filled' ? (
				<span className="embed-add-fv-label">
					<Check />
					{T('assistant.embedPersonFilled')}
				</span>
			) : state === 'nothingToFill' ? (
				<span className="embed-add-fv-label">{T('assistant.embedPersonNothingToFill')}</span>
			) : (
				// A failed save says so on the button itself, which tries again, rather than on a line above it.
				<button
					type="button"
					className="embed-add-btn"
					disabled={state === 'saving' || !(found.email || found.phone)}
					onClick={() => void fill()}
				>
					{T(state === 'error' ? 'assistant.embedAddSaveError' : 'assistant.embedPersonFillContact')}
				</button>
			)}
		</div>
	);
};

export default FillExistingContact;
